import type { ReactNode } from 'react'
import { useCan } from '@/auth/context'
import { Button, type ButtonProps } from '@/ui'

/** Renders children only when the user has the permission (or `fallback`). */
export function Can({ perm, children, fallback = null }: { perm: string | string[]; children: ReactNode; fallback?: ReactNode }) {
  const can = useCan()
  const perms = Array.isArray(perm) ? perm : [perm]
  return <>{perms.every(can) ? children : fallback}</>
}

export interface ActionButtonProps extends ButtonProps {
  /** Permission needed. Without it the button is hidden (`hide`) or disabled with an explanation. */
  perm?: string
  hide?: boolean
  /** Disable with this explanation (e.g. the two-person rule) — shown as title and sr text. */
  blockedReason?: string | null
}

/**
 * A console action button that knows its permission: hidden or disabled (with a tooltip saying
 * which permission is missing) for people who may not do it, and disabled with a reason when a
 * business rule blocks it.
 */
export function ActionButton({ perm, hide, blockedReason, disabled, title, children, ...rest }: ActionButtonProps) {
  const can = useCan()
  const allowed = perm ? can(perm) : true
  if (!allowed && hide) return null
  const reason = !allowed ? `You do not have permission (${perm}).` : (blockedReason ?? null)
  return (
    <Button {...rest} disabled={disabled || Boolean(reason)} title={reason ?? title} aria-description={reason ?? undefined}>
      {children}
    </Button>
  )
}
